// @ts-strict-ignore
import { Directive, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';

@Directive({
  standalone: true,
  selector: '[cvFocusTrap]',
})
export class CVFocusTrapDirective implements OnDestroy, OnChanges {
  @Input() 'cvFocusTrap';

  private _enabled: boolean;
  private _startAnchor: HTMLElement | null;
  private _endAnchor: HTMLElement | null;
  private _hasAttached: boolean = false;

  protected startAnchorListener = () => this.focusLastTabbableElement();
  protected endAnchorListener = () => this.focusFirstTabbableElement();

  constructor(private elementRef: ElementRef<HTMLElement>) {}
  ngOnChanges(changes: any): void {
    if (changes.cvFocusTrap.currentValue !== changes.cvFocusTrap.previousValue) {
      this._enabled =
        changes.cvFocusTrap.currentValue === undefined || changes.cvFocusTrap.currentValue === ''
          ? true
          : changes.cvFocusTrap.currentValue;
    }
    if (this._enabled) {
      this._attachAnchors();
      this.focusFirstTabbableElement();
    }
    this._toggleAnchorTabIndexes(this._enabled);
  }

  ngOnDestroy(): void {
    if (this._startAnchor) {
      this._startAnchor.removeEventListener('focus', this.startAnchorListener);
      this._startAnchor.remove();
    }
    if (this._endAnchor) {
      this._endAnchor.removeEventListener('focus', this.endAnchorListener);
      this._endAnchor.remove();
    }
  }

  focusFirstTabbableElement(): void {
    const firstTabbable = this._getFirstTabbableElement(this.elementRef.nativeElement);
    firstTabbable.focus();
  }
  focusLastTabbableElement(): void {
    const lastTabbable = this._getLastFocusableElement(this.elementRef.nativeElement) as HTMLElement | null;
    lastTabbable.focus();
  }

  private _attachAnchors(): void {
    if (this._hasAttached) {
      return;
    }
    if (!this._startAnchor) {
      this._startAnchor = document.createElement('div');
      this._startAnchor.tabIndex = 0;
      this._startAnchor.id = 'start-anchor';
      this._startAnchor.setAttribute('role', 'button');
      this._startAnchor.setAttribute('aria-label', '_');
      this._startAnchor.addEventListener('focus', this.startAnchorListener);
    }
    if (!this._endAnchor) {
      this._endAnchor = document.createElement('div');
      this._endAnchor.tabIndex = 0;
      this._endAnchor.id = 'end-anchor';
      this._endAnchor.setAttribute('role', 'button');
      this._endAnchor.setAttribute('aria-label', '_');
      this._endAnchor.addEventListener('focus', this.endAnchorListener);
    }
    this.elementRef.nativeElement.parentNode.insertBefore(this._startAnchor, this.elementRef.nativeElement);
    this.elementRef.nativeElement.parentNode.insertBefore(this._endAnchor, this.elementRef.nativeElement.nextSibling);
    this._hasAttached = true;
  }

  private _getFirstTabbableElement(element: HTMLElement): HTMLElement | null {
    if (element.tabIndex >= 0 && !element.hasAttribute('disabled')) {
      return element;
    }

    const children = element.children;

    for (let i = 0; i < children.length; i++) {
      const tabbableChild =
        children[i].nodeType === Node.ELEMENT_NODE ? this._getFirstTabbableElement(children[i] as HTMLElement) : null;

      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }

  private _getLastFocusableElement(element: HTMLElement): HTMLElement | null {
    if (element.tabIndex >= 0 && !element.hasAttribute('disabled')) {
      return element;
    }
    const children = element.children;

    for (let i = children.length - 1; i >= 0; i--) {
      const tabbableChild =
        children[i].nodeType === Node.ELEMENT_NODE ? this._getLastFocusableElement(children[i] as HTMLElement) : null;
      if (tabbableChild) {
        return tabbableChild;
      }
    }
  }

  private _toggleAnchorTabIndexes(enabled: boolean): void {
    if (this._startAnchor) {
      this._startAnchor.tabIndex = enabled ? 0 : -1;
    }
    if (this._endAnchor) {
      this._endAnchor.tabIndex = enabled ? 0 : -1;
    }
  }
}
